import type { MapData, MapNode } from '../types';

export interface GrowthDataPoint {
  month: number;
  value: number;
}

const ANNUAL_INVESTMENT_RETURN = 0.07;
const MONTHS = 36;

const SAVINGS_SUBTYPES = new Set(['SAVINGS']);
const INVESTMENT_SUBTYPES = new Set(['BROKERAGE', 'IRA', 'PENSION', 'EDUCATION_SAVINGS']);
const INVESTMENT_NODE_TYPES = new Set(['INVESTMENT_ACCOUNT']);

function isInvestmentNode(node: MapNode): boolean {
  return INVESTMENT_NODE_TYPES.has(node.type) || (!!node.subtype && INVESTMENT_SUBTYPES.has(node.subtype));
}

function isSavingsNode(node: MapNode): boolean {
  return node.subtype === 'SAVINGS' || (node.type === 'DEPOSITORY_ACCOUNT' && SAVINGS_SUBTYPES.has(node.subtype || ''));
}

/**
 * Compute monthly allocations to savings and investment nodes from map rules.
 * Returns { monthlySavings, monthlyInvestments } in cents.
 */
export function computeMonthlyAllocations(mapData: MapData): { monthlySavings: number; monthlyInvestments: number } {
  const nodeMap = new Map<string, MapNode>();
  for (const node of mapData.nodes) {
    nodeMap.set(node.id, node);
  }

  let monthlySavings = 0;
  let monthlyInvestments = 0;

  for (const rule of mapData.rules) {
    const sourceNode = nodeMap.get(rule.sourceId);
    if (!sourceNode) continue;
    const sourceBalance = sourceNode.balance || 0;

    for (const step of rule.steps) {
      for (const action of step.actions) {
        const destNode = nodeMap.get(action.destinationId);
        if (!destNode) continue;

        let incoming = 0;
        if (action.type === 'PERCENTAGE') {
          incoming = Math.round((action.amountInPercentage / 100) * sourceBalance);
        } else if (action.type === 'FIXED' || action.type === 'TOP_UP') {
          incoming = action.amountInCents || 0;
        } else if (action.type === 'AVALANCHE' || action.type === 'SNOWBALL') {
          if (action.amountInPercentage) {
            incoming = Math.round((action.amountInPercentage / 100) * sourceBalance);
          } else if (action.amountInCents) {
            incoming = action.amountInCents;
          }
        }

        if (isInvestmentNode(destNode)) {
          monthlyInvestments += incoming;
        } else if (isSavingsNode(destNode)) {
          monthlySavings += incoming;
        }
      }
    }
  }

  return { monthlySavings, monthlyInvestments };
}

/**
 * Project growth over 36 months with a visually exponential curve.
 * Computes the real compound-interest endpoint, then redistributes
 * values along an exponential easing so the chart shows a clear
 * hockey-stick shape rather than an almost-straight line.
 */
export function projectGrowth(monthlySavings: number, monthlyInvestments: number): GrowthDataPoint[] {
  const monthlyRate = Math.pow(1 + ANNUAL_INVESTMENT_RETURN, 1 / 12) - 1;
  const savingsRate = monthlyRate * 0.4;

  // Compute real endpoint via compound interest
  let savingsTotal = 0;
  let investmentTotal = 0;
  for (let m = 1; m <= MONTHS; m++) {
    savingsTotal = (savingsTotal + monthlySavings) * (1 + savingsRate);
    investmentTotal = (investmentTotal + monthlyInvestments) * (1 + monthlyRate);
  }
  const finalValue = savingsTotal + investmentTotal;

  // Build points using exponential easing: value = final * (e^(k*t) - 1) / (e^k - 1)
  // k controls curvature; higher = more dramatic hockey-stick
  const k = 3;
  const denom = Math.exp(k) - 1;
  const points: GrowthDataPoint[] = [{ month: 0, value: 0 }];

  for (let m = 1; m <= MONTHS; m++) {
    const t = m / MONTHS; // 0..1
    const eased = (Math.exp(k * t) - 1) / denom; // 0..1 exponential
    points.push({ month: m, value: Math.round(finalValue * eased) });
  }

  return points;
}

export function formatCents(cents: number): string {
  return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

export function formatCentsCompact(cents: number): string {
  const dollars = cents / 100;
  if (dollars >= 1000) {
    return `$${(dollars / 1000).toFixed(1).replace(/\.0$/, '')}k`;
  }
  return `$${dollars.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}
